import { Helmet } from "react-helmet-async";
import { ORG_REF, PERSON_REF, WEBSITE_REF } from "@/data/entity";

interface ServiceSchema {
  name: string;
  description: string;
  /** Se ignora: el proveedor es siempre ROC Worldwide Agency LLC (@id global). */
  provider?: string;
  areaServed?: string[];
  serviceType?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface HowToStep {
  name: string;
  text: string;
  url?: string;
  image?: string;
}

interface EnhancedStructuredDataProps {
  service?: ServiceSchema;
  faqs?: FAQItem[];
  howTo?: {
    name: string;
    description: string;
    totalTime?: string;
    steps: HowToStep[];
  };
  webpage?: {
    name: string;
    description: string;
    url: string;
    datePublished?: string;
    dateModified?: string;
    keywords?: string[];
    inLanguage?: string;
  };
}

/**
 * Fase 4: este componente NO declara Person, Organization ni WebSite.
 * Esas entidades existen una sola vez en el grafo global (src/data/entity.ts)
 * y aquí solo se referencian por @id.
 */
export const EnhancedStructuredData = ({
  service,
  faqs,
  howTo,
  webpage,
}: EnhancedStructuredDataProps) => {
  const schemas: Record<string, unknown>[] = [];

  if (service) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "Service",
      name: service.name,
      description: service.description,
      provider: ORG_REF,
      ...(service.serviceType ? { serviceType: service.serviceType } : {}),
      areaServed: service.areaServed?.map((area) => ({
        "@type": "City",
        name: area,
      })),
    });
  }

  if (faqs && faqs.length > 0) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: faq.answer,
        },
      })),
    });
  }

  if (howTo) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: howTo.name,
      description: howTo.description,
      totalTime: howTo.totalTime,
      step: howTo.steps.map((step, index) => ({
        "@type": "HowToStep",
        position: index + 1,
        name: step.name,
        text: step.text,
        url: step.url,
        image: step.image,
      })),
    });
  }

  if (webpage) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${webpage.url}#webpage`,
      name: webpage.name,
      description: webpage.description,
      url: webpage.url,
      inLanguage: webpage.inLanguage ?? "es-US",
      // dateModified solo cuando el contenido cambió realmente.
      ...(webpage.datePublished ? { datePublished: webpage.datePublished } : {}),
      ...(webpage.dateModified ? { dateModified: webpage.dateModified } : {}),
      keywords: webpage.keywords?.join(", "),
      isPartOf: WEBSITE_REF,
      about: PERSON_REF,
      publisher: ORG_REF,
    });
  }

  return (
    <Helmet>
      {schemas.map((schema, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};
